import * as XLSX from 'xlsx';

interface ConsolidatedCallData {
    userId: string;
    userName: string;
    totalOutboundCalls: number;
    totalInboundCalls: number;
    totalCalls: number;
    connectedCalls: number;
    missedCalls: number;
    uniqueCalls: number; // unique leads called (each lead counted once)
    totalDuration: number; // in seconds
    outboundDuration: number; // in seconds
    inboundDuration: number; // in seconds
    averageCallDurationTotal: number; // in seconds - includes all calls
    averageCallDurationConnected: number; // in seconds - connected calls only
    connectionRate: string; // percentage
    callAttemptTarget: number;
    targetAchievement: string; // percentage
}

export const exportCallDataToExcel = (
    consolidatedData: ConsolidatedCallData[],
    fileName: string = 'call_data_export'
) => {
    try {
        // Format the data for Excel
        const excelData = consolidatedData.map((data, index) => ({
            'S.No': index + 1,
            'User Name': data.userName,
            'Total Outbound Calls': data.totalOutboundCalls,
            'Total Inbound Calls': data.totalInboundCalls,
            'Total Calls': data.totalCalls,
            'Connected Calls': data.connectedCalls,
            'Missed Calls': data.missedCalls,
            'Unique Calls': data.uniqueCalls,
            'Connection Rate': data.connectionRate,
            // 'Total Duration (seconds)': data.totalDuration,
            'Total Duration (formatted)': formatDuration(data.totalDuration),
            // 'Outbound Duration (seconds)': data.outboundDuration,
            'Outbound Duration (formatted)': formatDuration(data.outboundDuration),
            // 'Inbound Duration (seconds)': data.inboundDuration,
            'Inbound Duration (formatted)': formatDuration(data.inboundDuration),
            // 'Avg Duration - All Calls (seconds)': data.averageCallDurationTotal,
            'Avg Duration - All Calls (formatted)': formatDuration(data.averageCallDurationTotal),
            // 'Avg Duration - Connected Calls (seconds)': data.averageCallDurationConnected,
            'Avg Duration - Connected Calls (formatted)': formatDuration(data.averageCallDurationConnected),
            'Call Attempt Target': data.callAttemptTarget,
            'Target Achievement': data.targetAchievement
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Set column widths for better readability
        const columnWidths = [
            { wch: 8 },   // S.No
            { wch: 20 },  // User Name
            { wch: 18 },  // Total Outbound Calls
            { wch: 18 },  // Total Inbound Calls
            { wch: 12 },  // Total Calls
            { wch: 15 },  // Connected Calls
            { wch: 12 },  // Missed Calls
            { wch: 12 },  // Unique Calls
            { wch: 15 },  // Connection Rate
            // { wch: 20 },  // Total Duration (seconds)
            { wch: 20 },  // Total Duration (formatted)
            // { wch: 22 },  // Outbound Duration (seconds)
            { wch: 22 },  // Outbound Duration (formatted)
            // { wch: 20 },  // Inbound Duration (seconds)
            { wch: 20 },  // Inbound Duration (formatted)
            // { wch: 28 },  // Avg Duration - All Calls (seconds)
            { wch: 28 },  // Avg Duration - All Calls (formatted)
            // { wch: 32 },  // Avg Duration - Connected Calls (seconds)
            { wch: 32 },  // Avg Duration - Connected Calls (formatted)
            { wch: 18 },  // Call Attempt Target
            { wch: 18 }   // Target Achievement
        ];
        worksheet['!cols'] = columnWidths;

        // Add the worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Call Data');

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const finalFileName = `${fileName}_${timestamp}.xlsx`;

        // Save the file
        XLSX.writeFile(workbook, finalFileName);

        return {
            success: true,
            fileName: finalFileName,
            recordCount: consolidatedData.length
        };
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

// Helper function to format duration from seconds to HH:MM:SS
const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
};

// Generate appropriate filename based on user selection and date range
export const generateExportFileName = (
    currentScreen: string,
    selectedUser: any[] | null,
    startDate: Date | null,
    endDate: Date | null,
    users: any[]
): string => {
    let baseName = 'call_data';
    
    // Add user context
    if (currentScreen === 'Staff') {
        baseName += '_staff';
    } else if (selectedUser && selectedUser.length > 0) {
        if (selectedUser.length === 1) {
            const userName = users.find(u => u.id === selectedUser[0].id)?.name || 'selected_user';
            baseName += `_${userName.toLowerCase().replace(/\s+/g, '_')}`;
        } else {
            baseName += '_selected_users';
        }
    } else if (currentScreen === 'Manager') {
        baseName += '_team';
    } else {
        baseName += '_all_staff';
    }
    
    // Add date range context
    if (startDate && endDate) {
        const startStr = startDate.toISOString().slice(0, 10);
        const endStr = endDate.toISOString().slice(0, 10);
        if (startStr === endStr) {
            baseName += `_${startStr}`;
        } else {
            baseName += `_${startStr}_to_${endStr}`;
        }
    } else {
        baseName += '_lifetime';
    }
    
    return baseName;
};
